// useUserAgent.ts
import { useMemo } from 'react'
import { useMediaQuery } from './useMediaQuery'

interface UserAgentInfo {
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  isIOS: boolean
  isAndroid: boolean
  userAgent: string
}

// Width at/below which we render the mobile shell regardless of user agent, so
// a narrow desktop window (or a resized browser) gets the mobile experience.
// Matches the FilterModal mobile breakpoint (max-width: 1023px).
const MOBILE_MAX_WIDTH = 1023
const MOBILE_VIEWPORT_QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`

function computeUserAgentInfo(isNarrowViewport: boolean): UserAgentInfo {
  if (typeof window === 'undefined') {
    return {
      isMobile: false,
      isTablet: false,
      isDesktop: true,
      isIOS: false,
      isAndroid: false,
      userAgent: '',
    }
  }

  const ua = navigator.userAgent

  const isAndroid = /Android/i.test(ua)
  const isIOS = /iPhone|iPad|iPod/i.test(ua)
  const isTablet = /(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)
  const isMobileUserAgent =
    /Mobile|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) &&
    !isTablet
  // Treat a narrow viewport as mobile too, so resizing a desktop browser below
  // the breakpoint switches to the mobile shell (FIR-7449).
  const isMobile = isMobileUserAgent || isNarrowViewport

  return {
    isMobile,
    isTablet,
    isDesktop: !isMobile && !isTablet,
    isIOS,
    isAndroid,
    userAgent: ua,
  }
}

export function useUserAgent(): UserAgentInfo {
  // matchMedia fires only when the breakpoint is actually crossed (not on every
  // resize tick), and the user-agent regexes never change after load — so this
  // recomputes at most when isNarrowViewport flips, avoiding a re-render storm
  // of the app shell during a window drag.
  const isNarrowViewport = useMediaQuery(MOBILE_VIEWPORT_QUERY)
  return useMemo(
    () => computeUserAgentInfo(isNarrowViewport),
    [isNarrowViewport]
  )
}
